/**
 * `v3.host.checks.*` — the loop host's side of the check pool, authenticated by its run
 * token ({@link V3HostMiddleware}), like `v3.host.spec` / `v3.host.append`. Payloads live in
 * `@fm/v3/checks`. Every call names the caller's own run; jobs are the caller's customer's.
 *
 * - `sourceBundleUrl` — a presigned PUT for a tree's source bundle, and whether it is
 *   already there.
 * - `enqueue` — validate a plan and queue its jobs, reusing any result with the same cache
 *   key (those come back terminal, with `reusedFrom`).
 * - `await` — a long-poll: the status of the named jobs once any is terminal, or after
 *   `waitSeconds`.
 *
 * A separate group from `V3HostRpcs` so its handlers live in their own router file.
 *
 * Handlers: `v3-host-checks.router.ts` (C3).
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import {
  CheckAwaitInput,
  CheckAwaitResult,
  CheckEnqueueInput,
  CheckEnqueueResult,
  CheckSourceUrl,
  CheckSourceUrlInput,
  CheckStorageError,
} from "@fm/v3/checks";
import { Schema } from "effect";
import { DatabaseError, ValidationError } from "../errors.ts";
import { V3HostMiddleware } from "./v3-host.tags.ts";

/**
 * @since 1.0.0
 */
export class V3HostCheckRpcs extends RpcGroup.make(
  Rpc.make("v3.host.checks.sourceBundleUrl", {
    payload: CheckSourceUrlInput,
    success: CheckSourceUrl,
    error: Schema.Union(ValidationError, CheckStorageError, DatabaseError),
  }).middleware(V3HostMiddleware),
  Rpc.make("v3.host.checks.enqueue", {
    payload: CheckEnqueueInput,
    success: CheckEnqueueResult,
    error: Schema.Union(ValidationError, CheckStorageError, DatabaseError),
  }).middleware(V3HostMiddleware),
  Rpc.make("v3.host.checks.await", {
    payload: CheckAwaitInput,
    success: CheckAwaitResult,
    error: Schema.Union(ValidationError, DatabaseError),
  }).middleware(V3HostMiddleware),
) {}
